// RÉFÉRENTIELS de préparamétrage (SA-10.1, maquette « reference lists ») — v2 alignée maquette :
// chaque liste est une TABLE MÉTIER (colonnes propres : rendu/clôture des moyens, période des saisons,
// domaine/obligatoire des motifs, abrégé des civilités, VALEURS ORDONNÉES des grilles…) avec le bouton
// « Ajouter un/une … » en tête et l'édition INLINE (libellé + attributs) ligne par ligne.
// ⚠️ Carve-out FISCAL : motifs verrouillés PAR TRIGGER en base — cadenas visible, refus réel restitué.
// La PROPAGATION (SA-10.2) est câblée : analyse par magasin, arbitrage des conflits, application +
// re-journalisation BO des canaux touchés — voir PropagationModal en fin de fichier.
import { useEffect, useId, useMemo, useState } from 'react';
import { alert, confirm, showToast, EmptyState, Icon, rovingRadio } from '@orgazone/ui-web';
import { api, type CommunesApplyResult, type CommunesStore, type PropagationApplyResult, type PropagationStoreDiff, type RefColDef, type RefDiffItem, type ReferentialRow, type RefListDef, type TradeTypeRow } from '../api';
import { useAuth, isSuperadmin } from '../auth';
import { TableScroll } from '../table-scroll';

// ── La définition MÉTIER d'une liste vient du SERVEUR (migration 0037) ────────────────────────────
// ⚠️ ELLE VIVAIT ICI, EN DUR — libellé, libellé du bouton, note, colonnes métier, flèches de rang —
//    et c'est précisément ce qui rendait impossible la CRÉATION d'un référentiel : « Millésimes »
//    chez un caviste demandait une livraison. Les explications qui accompagnaient chaque réglage
//    (`multiline` pour un message qui porte ses retours à la ligne, `choice` pour un vocabulaire
//    fermé, `orderable` seulement là où quelqu'un lit le rang) ont suivi la donnée : elles sont
//    commentées sur les colonnes de `referential_list`, là où le prochain lecteur les cherchera.
type ColDef = RefColDef;
type ListDef = RefListDef & {
  /** ⚠️ RANGEMENT EN BLOCS TITRÉS, propre aux modèles de consigne et resté du CÔTÉ ÉCRAN : chaque
   *  bloc porte la RÈGLE DE RÉDACTION qui vaut pour lui, c'est-à-dire un texte d'aide, pas une
   *  donnée de la liste. ⚠️ Il ne se combine pas avec les flèches : elles déplacent par indice dans
   *  la liste entière, et un indice ne veut plus rien dire une fois les lignes réparties. */
  groupBy?: { key: string; groups: { value: string; label: string; note: string }[] };
};
/** Les blocs des modèles de consigne — un texte d'aide par niveau, pas une donnée à éditer. */
const GROUP_BY: Record<string, NonNullable<ListDef['groupBy']>> = {
  modeles_consigne: {
    key: 'niveau',
    groups: [
      { value: 'warn', label: 'Avertissements', note: 'Rien ne s’arrête. Le texte doit dire ce qui va se passer, quand, et comment l’éviter — sans échéance ni remède, il ne sert à rien.' },
      { value: 'lock', label: 'Blocages', note: 'La caisse est arrêtée. Le texte doit dire qui appeler — l’opérateur sur place ne peut rien seul.' },
      { value: '', label: 'Sans formulation', note: 'Ne propose aucun texte : ouvre la saisie libre. Sert donc les deux niveaux.' },
    ],
  },
};
/**
 * La définition d'une clé. ⚠️ LE REPLI EXISTE ET IL LE DIT : une liste présente en DONNÉES mais sans
 * définition s'affiche quand même, sous son nom brut, avec « définition absente » en note. La faire
 * disparaître cacherait des valeurs réelles — c'est l'inverse de ce qu'on attend d'un référentiel.
 */
const defOf = (defs: Map<string, ListDef>, k: string): ListDef =>
  defs.get(k) ?? {
    key: k, label: k, addLabel: 'Ajouter', note: 'Définition absente : cette liste porte des valeurs, mais rien ne décrit ce qu’elle est.',
    category: '', cols: [], orderable: true, storeKind: null, builtin: false, ord: 9_999,
    descend: false, destination: 'définition absente',
  };
const attrOf = (r: ReferentialRow, key: string): unknown => (r as unknown as { attrs?: Record<string, unknown> }).attrs?.[key];
const attrText = (r: ReferentialRow, c: ColDef): string => {
  const v = attrOf(r, c.key);
  if (v == null) return '';
  if (c.kind === 'values' && Array.isArray(v)) return v.join(' · ');
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  return String(v);
};
/** Le libellé humain d'un choix fermé — la valeur STOCKÉE reste le code, jamais le libellé. */
const choiceLabel = (c: ColDef, valeur: string): string =>
  c.options?.find((o) => o.value === valeur)?.label ?? valeur;
/**
 * La teinte de la pastille d'un choix. Le niveau d'une consigne se REGARDE — c'est lui qui décide où le texte sera
 * proposé : ses deux valeurs prennent les couleurs du kill-switch, pour que l'œil fasse le lien.
 * ⚠️ Toute autre valeur reste NEUTRE : un axe de grille ou la nature d'un type d'équipement en orange se liraient
 *    comme un avertissement qui n'existe pas.
 */
export const choiceTone = (value: string): 'da' | 'wa' | 'ne' => (value === 'lock' ? 'da' : value === 'warn' ? 'wa' : 'ne');

/** Brouillon d'édition/ajout : libellé + un champ texte par colonne métier (valeurs = liste « · »). */
type Draft = Record<string, string>;
const draftFrom = (r: ReferentialRow, def: ListDef): Draft => {
  const d: Draft = { label: r.label };
  for (const c of def.cols) d[c.key] = attrText(r, c);
  return d;
};
const attrsFrom = (d: Draft, def: ListDef): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const c of def.cols) {
    // ⚠️ On ne taille que les EXTRÉMITÉS : les retours à la ligne INTERNES d'un message sont son sens.
    const raw = (d[c.key] ?? '').trim();
    if (!raw) continue;
    out[c.key] = c.kind === 'values' ? raw.split(/\s*[·,;]\s*/).filter(Boolean) : raw;
  }
  return out;
};

export function Referentiels() {
  const { user } = useAuth();
  const canWrite = isSuperadmin(user);
  const [rows, setRows] = useState<ReferentialRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; draft: Draft } | null>(null);
  const [adding, setAdding] = useState<Draft | null>(null); // null = formulaire fermé
  // Grilles de taille : édition en MODALE dédiée (l'ordre des valeurs EST la grille — un champ texte
  // « XS · S · M… » ne suffit pas). row=null → création. Décision UX 2026-08-05, reportée en maquette.
  const [gridModal, setGridModal] = useState<{ row: ReferentialRow | null } | null>(null);
  const [propagOpen, setPropagOpen] = useState(false); // flux SA-10.2 : analyse → arbitrage → application
  const [busy, setBusy] = useState(false);

  const [trades, setTrades] = useState<TradeTypeRow[] | null>(null);
  const [defs, setDefs] = useState<RefListDef[] | null>(null);
  const [socle, setSocle] = useState<string[]>([]);
  const [listWizard, setListWizard] = useState<{ def: RefListDef | null } | null>(null); // def=null → création
  const load = () => Promise.all([
    api.referentials().then((r) => { setRows(r.referentials); setErr(null); }),
    // ⚠️ Échec des types de commerce isolé : les listes restent utilisables, le panneau métiers le dit.
    api.referentialTrades().then((r) => setTrades(r.trades)).catch(() => setTrades(null)),
    // ⚠️ Échec des DÉFINITIONS non isolé, à l'inverse : sans elles l'écran ne connaît ni les libellés
    //    ni les colonnes, et rendrait dix listes nommées par leur clé technique. Mieux vaut dire
    //    l'erreur que servir une page qui a l'air de marcher.
    api.referentialDefinitions().then((r) => { setDefs(r.definitions); setSocle(r.socle); }),
  ]).catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);

  const lists = useMemo(() => {
    const m = new Map<string, ReferentialRow[]>();
    for (const r of rows ?? []) { const a = m.get(r.list) ?? []; a.push(r); m.set(r.list, a); }
    return m;
  }, [rows]);
  /** Les définitions par clé, avec le rangement en blocs que seul l'écran connaît. */
  const defsByKey = useMemo(() => {
    const m = new Map<string, ListDef>();
    for (const d of defs ?? []) m.set(d.key, { ...d, groupBy: GROUP_BY[d.key] });
    return m;
  }, [defs]);
  /**
   * Le rail. ⚠️ IL PART DES DÉFINITIONS, PLUS DES DONNÉES : auparavant une liste n'existait que
   * parce qu'elle avait des lignes, donc une liste tout juste créée était INTROUVABLE — impossible
   * d'y ajouter la première valeur. Les clés vues en données mais non définies suivent, pour que
   * rien ne se perde.
   */
  const listKeys = useMemo(() => {
    const ordered = (defs ?? []).map((d) => d.key);
    const orphelines = [...lists.keys()].filter((k) => !ordered.includes(k));
    return [...ordered, ...orphelines];
  }, [defs, lists]);
  const current = sel ?? listKeys[0] ?? null;
  const def = current ? defOf(defsByKey, current) : null;
  // ⚠️ Mémoïsée : calculée à la volée, elle rendait un tableau NEUF à chaque rendu, et le rangement en
  //    blocs qui en dépend se refaisait à chaque frappe dans un champ d'édition.
  const values = useMemo(() => (current ? (lists.get(current) ?? []) : []), [current, lists]);
  const cols = def?.cols ?? [];
  /** Les flèches de rang, seulement là où quelqu'un lit ce rang (voir `ordonnable` sur `ListDef`). */
  const orderable = def?.orderable !== false;
  /**
   * ⚠️ LA COLONNE « VERROU » N'APPARAÎT QUE S'IL EXISTE UN VERROU. Elle ne concerne que les motifs
   * fiscaux : partout ailleurs elle alignait une colonne de tirets, qui prenait la largeur dont le
   * texte avait besoin et faisait passer les actions à la ligne.
   */
  const hasLock = values.some((r) => r.locked);
  /** La liste rangée en blocs (`groupBy`), ou un bloc unique et sans titre. */
  const sections = useMemo(() => {
    const g = def?.groupBy;
    if (!g) return [{ title: null as string | null, note: '', rows: values }];
    return g.groups
      .map((grp) => ({
        title: grp.label, note: grp.note,
        rows: values.filter((r) => String(attrOf(r, g.key) ?? '') === grp.value),
      }))
      // Un bloc vide ne se rend pas : un titre sans ligne se lit comme un chargement raté.
      .filter((b) => b.rows.length > 0);
  }, [def, values]);

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    setBusy(true);
    try { await fn(); showToast(okMsg); await load(); return true; }
    catch (e) { void alert({ title: 'Action impossible', message: (e as Error).message }); return false; }
    finally { setBusy(false); }
  };

  const saveEdit = async (r: ReferentialRow) => {
    if (!editing || !def) return;
    const label = editing.draft.label?.trim();
    if (!label) return;
    const ok = await run(() => api.updateReferential(r.id, { label, attrs: attrsFrom(editing.draft, def) }), `« ${label} » enregistré.`);
    if (ok) setEditing(null);
  };
  const saveAdd = async () => {
    if (!adding || !current || !def) return;
    const code = adding.code?.trim(); const label = adding.label?.trim();
    if (!code || !label) return;
    const ok = await run(() => api.createReferential({ list: current, code, label, ord: (values.at(-1)?.ord ?? 0) + 1, attrs: attrsFrom(adding, def) } as Parameters<typeof api.createReferential>[0]), `« ${label} » ajouté.`);
    if (ok) setAdding(null);
  };
  const remove = async (r: ReferentialRow) => {
    const ok = await confirm({ title: `Supprimer « ${r.label} » ?`, message: `La valeur sort de la liste maître « ${def?.label ?? r.list} ». Les magasins déjà en service la gardent : la propagation ajoute et aligne, elle ne retire jamais une valeur qu’un article ou une pièce peut porter.`, confirmLabel: 'Supprimer' });
    if (ok) void run(() => api.deleteReferential(r.id), `« ${r.label} » supprimé.`);
  };
  const move = (idx: number, dir: -1 | 1) => {
    const a = values[idx]; const b = values[idx + dir];
    if (!a || !b) return;
    void run(async () => { await api.updateReferential(a.id, { ord: b.ord }); await api.updateReferential(b.id, { ord: a.ord }); }, 'Ordre mis à jour.');
  };

  if (err) return <div className="page"><div className="err">Erreur : {err}</div></div>;
  if (!rows) return <div className="page"><div className="muted">Chargement des référentiels…</div></div>;

  const thSt = { fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', background: 'var(--ink-50)', padding: '7px 12px' } as const;
  const inputSt = { height: 27, padding: '0 8px', borderRadius: 'var(--r-xs)', border: '1px solid var(--teal-500)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)', width: '100%' } as const;

  return (
    <div className="page ozin">
      {/* Titre et sous-titre SUIVENT le module choisi (maquette refTitle/refSubtitle) : le rail mêle des
          listes et deux modules qui n'ont rien à voir — un en-tête figé mentirait sur ce qu'on regarde. */}
      <div style={{ marginBottom: 16 }}>
        <h1 className="page-title">
          {current === '__security' ? 'Sécurité · liens' : current === '__trades' ? 'Types de commerce' : 'Référentiels par défaut'}
        </h1>
        {/* Sans contrainte de mesure — cf. Clients/Blocages : seule la reprise en porte une dans la maquette. */}
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>
          {current === '__security'
            ? "Combien de temps un lien émis reste valable — invitation, réinitialisation, mise en service d'un appareil."
            : current === '__trades'
              ? "Le corps de métier décide des listes injectées dans chaque nouvelle base magasin."
              : "Les listes maîtres qui préparamètrent les futurs magasins. Une édition ici vaut pour les magasins créés ENSUITE ; pour aligner les magasins en service, préparez une propagation (écart prévisualisé, magasin par magasin)."}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '250px minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
        {/* Rail des listes (collant, compteur mono). */}
        <div className="dcard" style={{ padding: 8, position: 'sticky', top: 66 }}>
          {/* Entrée ÉPINGLÉE « Sécurité · liens » (maquette, en tête du rail) — durées de validité. */}
          <button onClick={() => { setSel('__security'); setEditing(null); setAdding(null); }}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: current === '__security' ? 700 : 600, background: current === '__security' ? 'var(--ink-100)' : 'transparent', color: current === '__security' ? 'var(--ink900)' : 'var(--teal-600)' }}>
            <span style={{ flex: 1, textAlign: 'left' }}>Sécurité · liens</span>
            <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{SECURITY_LINKS.length}</span>
          </button>
          {/* Entrée ÉPINGLÉE « Types de commerce » (maquette refTrades) — l'ASSEMBLAGE des listes par métier. */}
          <button onClick={() => { setSel('__trades'); setEditing(null); setAdding(null); }}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: current === '__trades' ? 700 : 600, background: current === '__trades' ? 'var(--ink-100)' : 'transparent', color: current === '__trades' ? 'var(--ink900)' : 'var(--teal-600)' }}>
            <span style={{ flex: 1, textAlign: 'left' }}>Types de commerce</span>
            <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{trades?.length ?? '—'}</span>
          </button>
          <div style={{ height: 1, background: 'var(--ink200)', margin: '6px 2px 8px' }} />
          {listKeys.map((k) => (
            <button key={k} onClick={() => { setSel(k); setEditing(null); setAdding(null); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: current === k ? 700 : 500, background: current === k ? 'var(--ink-100)' : 'transparent', color: current === k ? 'var(--ink900)' : 'var(--ink600)' }}>
              <span style={{ flex: 1, textAlign: 'left' }}>{defOf(defsByKey, k).label}</span>
              <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{lists.get(k)?.length ?? 0}</span>
            </button>
          ))}
          {/* ⚠️ LE GESTE MANQUANT, ET C'ÉTAIT TOUT LE SUJET : jusqu'ici on ne pouvait ajouter qu'une
              VALEUR dans une liste existante. Créer « Millésimes » chez un caviste demandait une
              livraison. */}
          {canWrite && (
            <>
              <div style={{ height: 1, background: 'var(--ink200)', margin: '8px 2px 6px' }} />
              <button onClick={() => setListWizard({ def: null })}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 7, height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: 600, background: 'transparent', color: 'var(--teal-600)' }}>
                <Icon name="plus" size={13} /><span style={{ flex: 1, textAlign: 'left' }}>Nouvelle liste</span>
              </button>
            </>
          )}
        </div>

        {current === '__security' ? (
        <SecurityLinksPanel canWrite={canWrite} />
        ) : current === '__trades' ? (
        <TradesPanel trades={trades} lists={lists} defs={defs} socle={socle} canWrite={canWrite} onChanged={() => void load()} />
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="dcard" style={{ overflow: 'hidden' }}>
            {/* En-tête maquette : titre + note + BOUTON D'AJOUT propre à la liste. */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{def?.label ?? '—'}</span>
                  {/* ⚠️ LA DESTINATION EST DITE, ET ELLE VIENT DU SERVEUR. Sans elle, rien à l'écran ne
                      distingue une liste qui arrive dans les caisses d'une liste qui reste au siège —
                      et c'est la première question qu'on se pose en créant un référentiel. */}
                  {def && (
                    <span title={def.descend ? `Les valeurs de cette liste sont injectées dans la base des nouveaux magasins (${def.destination}).` : "Cette liste reste dans la Console : aucune valeur n'est injectée en magasin."}
                      style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--r-pill)', cursor: 'help', background: def.descend ? 'color-mix(in srgb, var(--teal-500) 12%, transparent)' : 'var(--ink-100)', color: def.descend ? 'var(--teal-700)' : 'var(--ink500)' }}>
                      {def.descend ? 'descend en magasin' : 'Console seulement'}
                    </span>
                  )}
                  {def?.builtin && <span title="Liste d'origine : d'autres parties du logiciel la nomment. Son nom technique et sa destination ne se modifient pas." style={{ color: 'var(--ink400)', display: 'grid', cursor: 'help' }}><Icon name="lock" size={11} /></span>}
                </div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, maxWidth: 560, lineHeight: 1.5 }}>{def?.note}</div>
              </div>
              <div style={{ flex: 1 }} />
              {canWrite && def && !current?.startsWith('__') && (
                <button onClick={() => setListWizard({ def })} className="ghost btn-ghost" style={{ width: 'auto', height: 32, padding: '0 11px', fontSize: 'var(--t-dense)' }}>Modifier la liste</button>
              )}
              {canWrite && (
                <button onClick={() => { if (current === 'size_grids') { setGridModal({ row: null }); } else { setAdding({ code: '', label: '' }); } setEditing(null); }} className="btn-ghost" style={{ height: 32, padding: '0 13px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-dense)', fontWeight: 600 }}>
                  {def?.addLabel ?? 'Ajouter'}
                </button>
              )}
            </div>

            {values.length === 0 && !adding ? <EmptyState icon="grid" title="Liste vide" note="Ajoutez une première valeur — elle servira de graine aux futurs magasins." /> : (
              <TableScroll minWidth={560}>
                  <thead>
                    <tr>
                      <th style={thSt}>Code</th>
                      <th style={thSt}>Libellé</th>
                      {/* ⚠️ AUCUNE LARGEUR IMPOSÉE, ET C'EST LE RÉSULTAT D'UNE MESURE. Élargir la colonne
                          du message paraissait évident — c'est le texte qu'on vient relire ici. Les deux
                          façons de le faire coûtent plus qu'elles ne rapportent : `width: 99%` fait casser
                          le libellé sur quatre lignes, un `minWidth` pousse « Supprimer » hors de l'écran.
                          La répartition naturelle laisse tout visible ; le défilement de `TableScroll`
                          couvre le reste. */}
                      {cols.map((c) => <th key={c.key} style={thSt}>{c.label}</th>)}
                      {hasLock && <th style={thSt}>Verrou</th>}
                      {canWrite && <th style={thSt} />}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Formulaire d'AJOUT en première ligne (ouvert par le bouton d'en-tête). */}
                    {canWrite && adding && def && (
                      <tr style={{ background: 'color-mix(in srgb, var(--teal-500) 6%, transparent)' }}>
                        <td style={{ padding: '7px 12px' }}><input autoFocus placeholder="code" className="mono" value={adding.code ?? ''} onChange={(e) => setAdding({ ...adding, code: e.target.value })} style={inputSt} /></td>
                        <td style={{ padding: '7px 12px' }}><input placeholder="libellé" value={adding.label ?? ''} onChange={(e) => setAdding({ ...adding, label: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') void saveAdd(); }} style={inputSt} /></td>
                        {cols.map((c) => (
                          <td key={c.key} style={{ padding: '7px 12px' }}>
                            {c.kind === 'multiline'
                              /* ⚠️ Pas de « Entrée = valider » ici : dans un message, Entrée fait une LIGNE. */
                              ? <textarea rows={3} placeholder={c.label.toLowerCase()} value={adding[c.key] ?? ''} onChange={(e) => setAdding({ ...adding, [c.key]: e.target.value })} style={{ ...inputSt, height: 'auto', padding: '6px 8px', lineHeight: 1.45, resize: 'vertical', fontFamily: 'inherit' }} />
                              : c.kind === 'choice'
                                /* ⚠️ Vocabulaire FERMÉ, et le vide reste possible : c'est le serveur qui dit
                                   si ce modèle a le droit de ne pas porter de niveau (message vide). */
                                ? <select aria-label={c.label} value={adding[c.key] ?? ''} onChange={(e) => setAdding({ ...adding, [c.key]: e.target.value })} style={inputSt}>
                                  <option value="">—</option>
                                  {c.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                                : <input placeholder={c.kind === 'values' ? 'XS · S · M…' : c.label.toLowerCase()} value={adding[c.key] ?? ''} onChange={(e) => setAdding({ ...adding, [c.key]: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') void saveAdd(); }} style={inputSt} />}
                          </td>
                        ))}
                        {hasLock && <td />}
                        <td style={{ padding: '7px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="btn btn-cta sm" disabled={busy || !adding.code?.trim() || !adding.label?.trim()} onClick={() => void saveAdd()} style={{ width: 'auto', marginRight: 5 }}>Ajouter</button>
                          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '4px 10px', fontSize: 'var(--t-meta)' }} onClick={() => setAdding(null)}>Annuler</button>
                        </td>
                      </tr>
                    )}
                    {sections.map((section) => [
                      /* ⚠️ LE TITRE PORTE LA RÈGLE DE RÉDACTION DU BLOC, pas seulement son nom : c'est ici
                         qu'on relit un texte pour juger s'il est au bon endroit, et la règle doit être
                         sous les yeux au moment du jugement plutôt que dans la note de la liste. */
                      section.title ? (
                        <tr key={`t-${section.title}`}>
                          <td colSpan={2 + cols.length + (hasLock ? 1 : 0) + (canWrite ? 1 : 0)}
                            style={{ padding: '16px 12px 7px', borderTop: '1px solid var(--ink100)' }}>
                            <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink900)', letterSpacing: '.01em' }}>{section.title}</span>
                            <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', marginLeft: 8 }}>{section.rows.length}</span>
                            <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink500)', lineHeight: 1.45, marginTop: 3, maxWidth: 720 }}>{section.note}</span>
                          </td>
                        </tr>
                      ) : null,
                      ...section.rows.map((r, i) => {
                      const isEd = editing?.id === r.id;
                      return (
                        <tr key={r.id} style={{ borderTop: '1px solid var(--ink100)' }}
                          onMouseEnter={(e) => { if (!isEd) (e.currentTarget as HTMLElement).style.background = 'var(--ink-50)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                              {current === 'couleurs_familles' && <span style={{ width: 13, height: 13, borderRadius: 'var(--r-r4)', background: `#${r.code}`, border: '1px solid var(--ink200)', flexShrink: 0 }} />}
                              <span className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink600)' }}>{r.code}</span>
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            {isEd ? <input aria-label={`Libellé de ${r.code}`} value={editing.draft.label ?? ''} onChange={(e) => setEditing({ id: r.id, draft: { ...editing.draft, label: e.target.value } })} onKeyDown={(e) => { if (e.key === 'Enter') void saveEdit(r); if (e.key === 'Escape') setEditing(null); }} style={inputSt} />
                              : <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{r.label}</span>}
                          </td>
                          {def?.cols.map((c) => (
                            <td key={c.key} style={{ padding: '8px 12px' }}>
                              {isEd ? (c.kind === 'multiline'
                                ? <textarea aria-label={c.label} rows={4} value={editing.draft[c.key] ?? ''} onChange={(e) => setEditing({ id: r.id, draft: { ...editing.draft, [c.key]: e.target.value } })} onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }} style={{ ...inputSt, height: 'auto', padding: '6px 8px', lineHeight: 1.45, resize: 'vertical', fontFamily: 'inherit' }} />
                                : c.kind === 'choice'
                                  ? <select aria-label={c.label} value={editing.draft[c.key] ?? ''} onChange={(e) => setEditing({ id: r.id, draft: { ...editing.draft, [c.key]: e.target.value } })} onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }} style={inputSt}>
                                    <option value="">—</option>
                                    {c.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                  : <input aria-label={c.label} value={editing.draft[c.key] ?? ''} onChange={(e) => setEditing({ id: r.id, draft: { ...editing.draft, [c.key]: e.target.value } })} onKeyDown={(e) => { if (e.key === 'Enter') void saveEdit(r); if (e.key === 'Escape') setEditing(null); }} style={inputSt} />)
                                : c.kind === 'values' ? (() => {
                                  // Valeurs ordonnées en PASTILLES (l'ordre se lit) — l'édition passe par la modale.
                                  const v = attrOf(r, c.key);
                                  return Array.isArray(v) && v.length
                                    ? <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>{v.map((x, j) => <span key={j} className="mono" style={{ fontSize: 'var(--t-sub)', padding: '2px 7px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink700)' }}>{String(x)}</span>)}</span>
                                    : <span style={{ color: 'var(--ink400)' }}>—</span>;
                                })()
                                : c.kind === 'choice' ? (() => {
                                  const v = attrText(r, c);
                                  if (!v) return <span style={{ color: 'var(--ink400)' }}>—</span>;
                                  const tone = choiceTone(v);
                                  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-sub)', fontWeight: 700, whiteSpace: 'nowrap', background: `var(--${tone}-bg)`, color: `var(--${tone}-fg)` }}>{choiceLabel(c, v)}</span>;
                                })()
                                : c.kind === 'multiline'
                                  /* Le texte se lit COMME IL SERA VU en caisse — retours à la ligne compris. */
                                  ? <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink700)', whiteSpace: 'pre-wrap', display: 'block', maxWidth: 560, lineHeight: 1.45 }}>{attrText(r, c) || <span style={{ color: 'var(--ink400)' }}>—</span>}</span>
                                  : <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink700)' }}>{attrText(r, c) || <span style={{ color: 'var(--ink400)' }}>—</span>}</span>}
                            </td>
                          ))}
                          {hasLock && <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                            {r.locked
                              ? <span title="Motif fiscal (chaîne/JET) : ni renommage, ni suppression — seul l'ordre est réglable." style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-sub)', fontWeight: 700, color: 'var(--wa-fg)', cursor: 'help' }}><Icon name="lock" size={12} /> fiscal</span>
                              : <span style={{ color: 'var(--ink300)' }}>—</span>}
                          </td>}
                          {canWrite && (
                            <td style={{ padding: '8px 12px' }}>
                              {/* ⚠️ UNE RANGÉE, PAS UNE PILE. `.btn-icon` est un bloc `grid` : posés les uns
                                  à la suite, ces boutons passaient chacun à la ligne — deux flèches
                                  empilées au-dessus de deux boutons, sur toutes les lignes de toutes les
                                  listes. `white-space: nowrap` n'y pouvait rien : ce ne sont pas des mots. */}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                                {orderable && (
                                  <>
                                    <button disabled={busy || i === 0} onClick={() => move(i, -1)} title="Monter" className="btn-icon sm" style={{ color: i === 0 ? 'var(--ink300)' : 'var(--ink600)', cursor: i === 0 ? 'default' : 'pointer' }}><Icon name="chevronU" size={13} /></button>
                                    <button disabled={busy || i === values.length - 1} onClick={() => move(i, 1)} title="Descendre" className="btn-icon sm" style={{ color: i === values.length - 1 ? 'var(--ink300)' : 'var(--ink600)', cursor: i === values.length - 1 ? 'default' : 'pointer', marginRight: 4 }}><Icon name="chevronD" size={13} /></button>
                                  </>
                                )}
                                {isEd ? (
                                  <>
                                    <button className="btn btn-cta sm" disabled={busy} onClick={() => void saveEdit(r)} style={{ width: 'auto' }}>Enregistrer</button>
                                    <button className="ghost btn-ghost sm" style={{ width: 'auto' }} onClick={() => setEditing(null)}>Annuler</button>
                                  </>
                                ) : current === 'size_grids' ? (
                                  /* L'ordre des valeurs EST la grille : ce geste ouvre un éditeur, il se nomme. */
                                  <button disabled={busy || r.locked} onClick={() => setGridModal({ row: r })} className="ghost btn-ghost sm" style={{ width: 'auto', opacity: r.locked ? 0.45 : 1 }}>Personnaliser</button>
                                ) : (
                                  <>
                                    {/* ⚠️ DEUX ICÔNES PLUTÔT QUE DEUX MOTS : répétés à chaque ligne, « Modifier »
                                        et « Supprimer » prenaient plus de place que la donnée. Le titre reste,
                                        pour le survol comme pour un lecteur d'écran. */}
                                    <button disabled={busy || r.locked} title="Modifier" aria-label={`Modifier ${r.label}`}
                                      onClick={() => { if (def) setEditing({ id: r.id, draft: draftFrom(r, def) }); }}
                                      className="btn-icon sm" style={{ opacity: r.locked ? 0.4 : 1, cursor: r.locked ? 'default' : 'pointer' }}><Icon name="edit" size={13} /></button>
                                    <button disabled={busy || r.locked} title="Supprimer" aria-label={`Supprimer ${r.label}`}
                                      onClick={() => void remove(r)}
                                      className="btn-icon sm danger" style={{ opacity: r.locked ? 0.4 : 1, cursor: r.locked ? 'default' : 'pointer' }}><Icon name="trash" size={13} /></button>
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })])}
                  </tbody>
              </TableScroll>
            )}
            {!canWrite && <div style={{ padding: '10px 16px', borderTop: '1px solid var(--ink200)', fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Lecture seule — l'édition des listes maîtres est réservée au super-admin.</div>}
          </div>

          {/* Carte Propagation (SA-10.2, seconde moitié) — le flux RÉEL : analyse par magasin, arbitrage
              des conflits, application + re-journalisation BO. Réservée à l'écriture référentiels. */}
          <div className="dcard" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>Propagation aux clients existants</div>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5 }}>Action explicite, magasin par magasin, avec prévisualisation des écarts. Par défaut, une édition ici ne touche que les <b style={{ color: 'var(--ink700)' }}>nouveaux</b> magasins.</div>
            </div>
            <button onClick={() => setPropagOpen(true)} disabled={!canWrite}
              title={canWrite ? undefined : "Réservée au super-admin — l'analyse ouvre la base de chaque magasin."}
              className="btn-cta" style={{ height: 34, padding: '0 14px', borderRadius: 'var(--r-sm)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', ...(canWrite ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}>
              Préparer une propagation
            </button>
          </div>

          <CommunesPanel canWrite={canWrite} />
        </div>
        )}
      </div>
      {propagOpen && <PropagationModal onClose={() => setPropagOpen(false)} />}
      {gridModal && (
        <GridModal row={gridModal.row} nextOrd={(lists.get('size_grids')?.at(-1)?.ord ?? 0) + 1}
          onClose={() => setGridModal(null)}
          onSaved={(msg) => { setGridModal(null); showToast(msg); void load(); }} />
      )}
      {listWizard && (
        <ListWizard def={listWizard.def}
          categories={[...new Set((defs ?? []).map((d) => d.category).filter(Boolean))]}
          valeurs={listWizard.def ? (lists.get(listWizard.def.key)?.length ?? 0) : 0}
          onClose={() => setListWizard(null)}
          onSaved={(msg, cle) => { setListWizard(null); showToast(msg); if (cle) setSel(cle); void load(); }} />
      )}
    </div>
  );
}

// ═══ CRÉER UNE LISTE DE RÉFÉRENCE — le geste qui manquait (demande utilisateur 2026-09-08) ════════
//
// Jusqu'ici on ne pouvait ajouter qu'une VALEUR dans une liste existante : « Millésimes » chez un
// caviste, là où le prêt-à-porter a « Saisons », demandait une livraison. Ce formulaire pose les
// quatre choses qui ont chacune un lecteur réel — comment la liste s'appelle, comment on l'invite à
// s'enrichir, où elle se classe dans les types de commerce, et ce qu'elle porte en plus du code.
//
// ⚠️ DEUX CHAMPS SONT DES IDENTITÉS ET NE SE MODIFIENT JAMAIS APRÈS COUP : le nom technique et la
//    famille de descente. `referential.list` et `trade_type.lists` portent le premier ; les
//    identifiants DÉTERMINISTES des lignes déjà envoyées en magasin dérivent du second. Les changer
//    orphelinerait ce qui est parti — la création est le seul moment où ils sont gratuits, et c'est
//    pourquoi ils se saisissent ici plutôt que de se déduire en silence.
//
// ⚠️ CE FORMULAIRE NE DEMANDE PAS LE TITRE DE RUBRIQUE (« Millésime » à la place de « Saison » dans
//    les colonnes du back-office). Rien ne le lit encore : offrir le champ ferait croire que le
//    renommage a eu lieu (§23.0, « option offerte non implémentée »). Il viendra branché de bout en
//    bout — voir `docs/CADRAGE-Referentiels-Vocabulaire.md` §6.
function ListWizard({ def, categories, valeurs, onClose, onSaved }: {
  /** null = création. Sinon édition : la clé et la descente sont figées, le reste s'ajuste. */
  def: RefListDef | null;
  /** Les sections déjà utilisées, pour ranger au même endroit plutôt qu'en créer une jumelle. */
  categories: string[];
  /** Nombre de valeurs déjà dans la liste — c'est lui qui autorise ou refuse la suppression. */
  valeurs: number;
  onClose: () => void;
  onSaved: (msg: string, cle?: string) => void;
}) {
  const creation = def === null;
  /** Le préfixe des identifiants qui relient chaque libellé à son champ — unique par modale ouverte. */
  const uid = useId();
  const [label, setLabel] = useState(def?.label ?? '');
  const [key, setKey] = useState(def?.key ?? '');
  const [keyTouched, setKeyTouched] = useState(!creation);
  const [addLabel, setAddLabel] = useState(def?.addLabel ?? '');
  const [note, setNote] = useState(def?.note ?? '');
  const [category, setCategory] = useState(def?.category ?? '');
  const [descend, setDescend] = useState(def ? Boolean(def.storeKind) : true);
  const [storeKind, setStoreKind] = useState(def?.storeKind ?? '');
  const [kindTouched, setKindTouched] = useState(!creation);
  const [cols, setCols] = useState<RefColDef[]>(def?.cols ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /**
   * Le nom technique PROPOSÉ depuis le libellé — accents retirés, espaces en tirets bas.
   * ⚠️ Proposé, jamais imposé : dès que l'utilisateur y touche, on cesse de le réécrire sous ses
   *    doigts. Une clé qui change toute seule pendant qu'on la corrige est la meilleure façon de
   *    laisser partir en base celle qu'on ne voulait pas.
   */
  const technique = (v: string): string =>
    v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 61);
  const onLabel = (v: string) => {
    setLabel(v);
    if (creation && !keyTouched) setKey(technique(v));
    if (creation && !kindTouched) setKindTouched(false);
  };
  // La famille est le SINGULIER de la clé, faute de mieux : `millesimes` → `millesime`. Proposition,
  // corrigeable — c'est elle qui nomme la famille dans la base des magasins.
  const kindPropose = key.replace(/s$/, '');
  const kindFinal = (kindTouched ? storeKind : kindPropose).trim();

  const keyErr = !key ? null
    : !/^[a-z][a-z0-9_]{1,60}$/.test(key) ? 'Minuscules, chiffres et tirets bas, en commençant par une lettre.' : null;
  const ready = !busy && !!label.trim() && !!key && !keyErr && (!descend || !!kindFinal);

  const setCol = (i: number, patch: Partial<RefColDef>) => setCols(cols.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const addCol = () => setCols([...cols, { key: '', label: '', kind: 'text' }]);
  const delCol = (i: number) => setCols(cols.filter((_, j) => j !== i));
  // Une colonne sans intitulé n'affiche rien : sa clé technique se propose depuis son intitulé, comme
  // celle de la liste, et le serveur écarte de toute façon les colonnes incomplètes.
  const colsPrets = cols
    .map((c) => ({ ...c, key: c.key || technique(c.label), label: c.label.trim() }))
    .filter((c) => c.key && c.label);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const commun = { label: label.trim(), addLabel: addLabel.trim(), note: note.trim(), category: category.trim(), cols: colsPrets };
      if (def) {
        await api.updateRefList(def.key, commun);
        onSaved(`Liste « ${label.trim()} » enregistrée.`, def.key);
      } else {
        await api.createRefList({ ...commun, key, storeKind: descend ? kindFinal : null });
        onSaved(`Liste « ${label.trim()} » créée.`, key);
      }
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  const supprimer = async () => {
    const ok = await confirm({
      title: `Supprimer la liste « ${def!.label} » ?`,
      message: "La liste disparaît de la Console et des types de commerce. ⚠️ Les magasins DÉJÀ EN SERVICE gardent les valeurs qu'ils ont reçues : rien n'est retiré d'une base en service.",
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    setBusy(true); setErr(null);
    try { await api.deleteRefList(def!.key); onSaved(`Liste « ${def!.label} » supprimée.`); }
    catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  const inSt = { height: 30, padding: '0 10px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', width: '100%' } as const;
  const labSt = { display: 'block', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink500)', marginBottom: 4, fontWeight: 700 } as const;
  const aideSt = { fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5 } as const;

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{ width: 'min(94vw, 620px)', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>{def ? `Modifier « ${def.label} »` : 'Nouvelle liste de référence'}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 16 }}>
          Une liste maître de l'éditeur : elle préparamètre les futurs magasins du ou des métiers qui la cochent.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor={`${uid}-label`} style={labSt}>Libellé</label>
          <input id={`${uid}-label`} autoFocus value={label} onChange={(e) => onLabel(e.target.value)} placeholder="Millésimes" style={inSt} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor={`${uid}-key`} style={labSt}>Nom technique</label>
          <input id={`${uid}-key`} value={key} disabled={!creation} className="mono"
            onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }}
            placeholder="millesimes" style={{ ...inSt, ...(creation ? {} : { background: 'var(--ink-50)', color: 'var(--ink500)' }) }} />
          <div style={aideSt}>
            {creation
              ? "⚠️ Il ne se modifiera plus après la création : les valeurs, les types de commerce et les lignes déjà envoyées en magasin s'y réfèrent par ce nom."
              : "Figé : c'est par ce nom que les valeurs, les types de commerce et les magasins déjà servis désignent cette liste."}
          </div>
          {keyErr && <div style={{ ...aideSt, color: 'var(--da-fg)' }}>{keyErr}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 10, marginBottom: 12 }}>
          <div>
            <label htmlFor={`${uid}-add`} style={labSt}>Libellé du bouton d'ajout</label>
            <input id={`${uid}-add`} value={addLabel} onChange={(e) => setAddLabel(e.target.value)} placeholder="Ajouter un millésime" style={inSt} />
            <div style={aideSt}>Vide, il dira « Ajouter » — les autres listes disent ce qu'elles ajoutent.</div>
          </div>
          <div>
            <label htmlFor={`${uid}-category`} style={labSt}>Classement</label>
            <input id={`${uid}-category`} value={category} onChange={(e) => setCategory(e.target.value)} list="ref-cats" placeholder="Organisation" style={inSt} />
            <datalist id="ref-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            <div style={aideSt}>La section du panneau « Types de commerce » où la liste se coche. <strong>Vide, elle ne s'y montre pas</strong> et ne peut être rattachée à aucun métier.</div>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label htmlFor={`${uid}-note`} style={labSt}>Note</label>
          <input id={`${uid}-note`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="À quoi sert cette liste, en une phrase." style={inSt} />
          <div style={aideSt}>Affichée sous le titre de la liste. C'est ce que lira celui qui la remplira.</div>
        </div>

        {/* ── LA DESCENTE ─────────────────────────────────────────────────────────────────────── */}
        <div style={{ padding: '11px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: creation ? 'pointer' : 'default' }}>
            <input type="checkbox" checked={descend} disabled={!creation} onChange={(e) => setDescend(e.target.checked)} />
            <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>Les valeurs descendent dans les magasins</span>
          </label>
          <div style={aideSt}>
            Cochée, la liste est injectée dans la base de chaque nouveau magasin du métier, et la
            propagation peut l'envoyer aux magasins existants. Décochée, elle ne quitte pas la Console
            — c'est le cas des codes NAF et des modèles de consigne.
          </div>
          {descend && (
            <div style={{ marginTop: 10 }}>
              <label htmlFor={`${uid}-kind`} style={labSt}>Famille en magasin</label>
              <input id={`${uid}-kind`} value={kindTouched ? storeKind : kindPropose} disabled={!creation} className="mono"
                onChange={(e) => { setKindTouched(true); setStoreKind(e.target.value.toLowerCase()); }}
                placeholder="millesime" style={{ ...inSt, ...(creation ? {} : { background: 'var(--ink-50)', color: 'var(--ink500)' }) }} />
              <div style={aideSt}>
                {creation
                  ? "⚠️ Elle non plus ne se modifiera pas : l'identifiant de chaque valeur envoyée en magasin en dérive. Au singulier, par convention."
                  : 'Figée : les identifiants des valeurs déjà envoyées en magasin en dérivent.'}
              </div>
            </div>
          )}
        </div>

        {/* ── LES COLONNES MÉTIER ─────────────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span id={`${uid}-cols`} className="field-title" style={{ ...labSt, marginBottom: 0 }}>Colonnes métier</span>
          <div style={{ flex: 1 }} />
          {cols.length < 6 && <button onClick={addCol} className="ghost btn-ghost" style={{ width: 'auto', padding: '3px 9px', fontSize: 'var(--t-meta)' }}>Ajouter une colonne</button>}
        </div>
        <div style={{ ...aideSt, marginTop: 0, marginBottom: 8 }}>
          Ce que la liste porte <strong>en plus</strong> du code et du libellé — les saisons ont une
          « Période », les motifs un « Domaine ». Aucune colonne convient parfaitement à une liste de
          codes.
        </div>
        {/* Le titre de section nomme le GROUPE de lignes : chaque ligne a ses propres champs, il n'en désigne aucun. */}
        <div role="group" aria-labelledby={`${uid}-cols`}>
        {cols.map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: 7, marginBottom: 7, alignItems: 'center' }}>
            <input aria-label={`Intitulé de la colonne ${i + 1}`} value={c.label} onChange={(e) => setCol(i, { label: e.target.value })} placeholder="Intitulé de colonne" style={{ ...inSt, flex: 1 }} />
            <select aria-label={`Type de la colonne ${i + 1}`} value={c.kind} onChange={(e) => setCol(i, { kind: e.target.value as RefColDef['kind'] })} style={{ ...inSt, width: 150 }}>
              <option value="text">Texte</option>
              <option value="values">Valeurs ordonnées</option>
              <option value="multiline">Texte long</option>
            </select>
            <button onClick={() => delCol(i)} className="btn-ghost danger" style={{ height: 30, padding: '0 9px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontWeight: 600 }}>Retirer</button>
          </div>
        ))}
        </div>

        {err && <div className="err" style={{ marginTop: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, alignItems: 'center' }}>
          {def && !def.builtin && (
            <button onClick={() => void supprimer()} disabled={busy || valeurs > 0}
              title={valeurs > 0 ? `Cette liste porte ${valeurs} valeur(s) : videz-la d'abord, pour que la suppression ne les emporte pas sans le dire.` : undefined}
              className="btn-ghost danger" style={{ height: 32, padding: '0 11px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-dense)', fontWeight: 600, ...(busy || valeurs > 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
              Supprimer
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} disabled={busy} className="ghost btn-ghost" style={{ width: 'auto', height: 32, padding: '0 13px' }}>Annuler</button>
          <button onClick={() => void submit()} disabled={!ready} className="btn btn-cta" style={{ width: 'auto', height: 32, padding: '0 15px' }}>
            {def ? 'Enregistrer' : 'Créer la liste'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ MODALE D'ÉDITION D'UNE GRILLE DE TAILLE — l'ordre des valeurs EST la grille : chaque valeur est
// une ligne éditable, réordonnable (↑↓), supprimable, avec ajout en fin. Bien plus intuitif que le
// champ texte « XS · S · M… » (relevé utilisateur 2026-08-05, reporté en maquette). row=null → création
// (le code se saisit) ; en édition le code est l'IDENTITÉ de la grille (référencée par les types de
// commerce via 'size_grids:<code>') donc non modifiable.
// ⚠️ UNE SEULE LISTE POUR LES DEUX AXES : la grille dit si elle porte des TAILLES (axe principal) ou un
// SECOND AXE (longueur, bonnet…) avec son libellé de rubrique. Aucune grille n'en rattache une autre : le
// second axe se choisit sur la fiche de chaque modèle, et la base magasin n'a plus de colonne pour ce lien.
// ⚠️ LA CLÉ DES VALEURS EST `values`, LUE ET ÉCRITE : l'écran lisait `values` et écrivait `valeurs` —
// personnaliser une grille l'ouvrait vide, et l'enregistrement remplaçait ses valeurs par celles qu'on
// venait de retaper. La migration 0042 a rangé la clé, ce fichier ne doit plus jamais en connaître une autre. ═══
function GridModal({ row, nextOrd, onClose, onSaved }: {
  row: ReferentialRow | null;
  nextOrd: number;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const initial = row?.attrs?.values;
  /** Le préfixe des identifiants qui relient chaque libellé à son champ — unique par modale ouverte. */
  const uid = useId();
  const [code, setCode] = useState(row?.code ?? '');
  const [label, setLabel] = useState(row?.label ?? '');
  const [values, setValues] = useState<string[]>(Array.isArray(initial) ? initial.map(String) : []);
  const [axis, setAxis] = useState<'primary' | 'secondary'>(row?.attrs?.axis === 'secondary' ? 'secondary' : 'primary');
  const [dimensionLabel, setDimensionLabel] = useState(typeof row?.attrs?.dimension_label === 'string' ? row.attrs.dimension_label : '');
  const [draft, setDraft] = useState(''); // champ « ajouter une valeur »
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setAt = (i: number, v: string) => setValues(values.map((x, j) => (j === i ? v : x)));
  const moveAt = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= values.length) return;
    const next = [...values]; [next[i], next[j]] = [next[j]!, next[i]!];
    setValues(next);
  };
  const removeAt = (i: number) => setValues(values.filter((_, j) => j !== i));
  const append = () => {
    const v = draft.trim();
    if (!v) return;
    setValues([...values, v]); setDraft('');
  };

  const cleaned = values.map((v) => v.trim()).filter(Boolean);
  const dupes = cleaned.filter((v, i) => cleaned.indexOf(v) !== i);
  const ready = !busy && !!label.trim() && (!!row || !!code.trim()) && cleaned.length > 0 && dupes.length === 0;

  /** Les attributs tels que la base magasin les attend : l'axe, ses valeurs, et ce que l'axe implique. */
  const attrs = {
    ...(row?.attrs ?? {}),
    axis,
    values: cleaned,
    dimension_label: axis === 'secondary' ? (dimensionLabel.trim() || null) : null,
  };
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (row) {
        await api.updateReferential(row.id, { label: label.trim(), attrs });
        onSaved(`Grille « ${label.trim()} » enregistrée.`);
      } else {
        await api.createReferential({ list: 'size_grids', code: code.trim(), label: label.trim(), ord: nextOrd, attrs });
        onSaved(`Grille « ${label.trim()} » créée.`);
      }
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  const inSt = { height: 30, padding: '0 10px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-body)', width: '100%' } as const;
  const arrowSt = (off: boolean) => ({ width: 26, height: 26, borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'transparent', color: off ? 'var(--ink300)' : 'var(--ink600)', cursor: off ? 'default' : 'pointer', flexShrink: 0 } as const);

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{ width: 'min(94vw, 460px)' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>{row ? `Personnaliser « ${row.label} »` : 'Nouvelle grille de taille'}</h3>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 14 }}>L'ordre des valeurs EST la grille : il sert de graine aux nouveaux magasins et pilote l'ordre d'affichage en caisse.</div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ width: 130 }}>
            <label htmlFor={`${uid}-code`} style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 }}>Code</label>
            <input id={`${uid}-code`} className="mono" value={code} disabled={!!row} placeholder="pointures"
              title={row ? "Le code est l'identité de la grille (les types de commerce s'y réfèrent) — non modifiable." : undefined}
              onChange={(e) => setCode(e.target.value)} style={{ ...inSt, opacity: row ? 0.55 : 1 }} />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor={`${uid}-label`} style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 }}>Libellé</label>
            <input id={`${uid}-label`} value={label} placeholder="Pointures (36 → 46)" autoFocus={!row} onChange={(e) => setLabel(e.target.value)} style={inSt} />
          </div>
        </div>

        {/* L'AXE décide ce que la grille porte : des tailles, ou un second axe que chaque modèle choisit sur sa fiche. */}
        <span id={`${uid}-axis`} className="field-title" style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 }}>Axe</span>
        <div role="radiogroup" aria-labelledby={`${uid}-axis`} style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {([['primary', 'Tailles (axe principal)'], ['secondary', 'Second axe (longueur, bonnet…)']] as const).map(([v, l], i) => (
            <button key={v} type="button" role="radio" aria-checked={axis === v} disabled={busy} onClick={() => setAxis(v)}
              {...rovingRadio({ index: i, count: 2, checked: axis === v, anyChecked: true, onPick: (k) => setAxis(k === 0 ? 'primary' : 'secondary') })}
              className={axis === v ? 'btn btn-cta sm' : 'ghost btn-ghost sm'} style={{ width: 'auto', padding: '0 12px' }}>{l}</button>
          ))}
        </div>
        {axis === 'secondary' && (
          <div style={{ marginBottom: 12 }}>
            <label htmlFor={`${uid}-dimension`} style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 }}>Libellé de la rubrique</label>
            <input id={`${uid}-dimension`} value={dimensionLabel} placeholder="Longueur" onChange={(e) => setDimensionLabel(e.target.value)} style={inSt}
              title="Ce que la matrice écrit en tête de ses lignes : « Longueur » pour un jean, « Bonnet » pour un soutien-gorge." />
          </div>
        )}
        <span id={`${uid}-values`} className="field-title" style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 }}>Valeurs (dans l'ordre)</span>
        <div role="group" aria-labelledby={`${uid}-values`} style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', padding: 6, maxHeight: 260, overflowY: 'auto', marginBottom: 8 }}>
          {values.length === 0 && <div style={{ padding: '10px 8px', fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>Aucune valeur — ajoutez la première ci-dessous.</div>}
          {values.map((v, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 2px' }}>
              <span className="mono" style={{ width: 22, textAlign: 'right', fontSize: 'var(--t-sub)', color: 'var(--ink400)', flexShrink: 0 }}>{i + 1}</span>
              <input aria-label={`Valeur ${i + 1}`} className="mono" value={v} onChange={(e) => setAt(i, e.target.value)} style={{ ...inSt, height: 28, fontSize: 'var(--t-dense)' }} />
              <button disabled={busy || i === 0} onClick={() => moveAt(i, -1)} title="Monter" style={arrowSt(i === 0)}>↑</button>
              <button disabled={busy || i === values.length - 1} onClick={() => moveAt(i, 1)} title="Descendre" style={arrowSt(i === values.length - 1)}>↓</button>
              <button disabled={busy} onClick={() => removeAt(i)} title="Retirer" className="btn-icon danger">✕</button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <input value={draft} placeholder="nouvelle valeur (ex. 47)" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') append(); }} style={inSt} />
          <button disabled={busy || !draft.trim()} onClick={append} className="ghost btn-ghost" style={{ width: 'auto', padding: '0 13px', fontSize: 'var(--t-dense)', whiteSpace: 'nowrap' }}>Ajouter</button>
        </div>

        {cleaned.length > 0 && (
          <div className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginBottom: 10, lineHeight: 1.6 }}>Aperçu : {cleaned.join(' · ')}</div>
        )}
        {dupes.length > 0 && <div style={{ fontSize: 'var(--t-note)', color: 'var(--wa-fg)', marginBottom: 10 }}>Valeur en double : {[...new Set(dupes)].join(', ')} — une grille ne répète pas une taille.</div>}
        {err && <div className="err" style={{ marginBottom: 10 }}>Erreur : {err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '6px 13px', fontSize: 'var(--t-dense)' }} disabled={busy} onClick={onClose}>Annuler</button>
          <button className="btn btn-cta sm" style={{ width: 'auto' }} disabled={!ready} onClick={() => void submit()}>{row ? 'Enregistrer' : 'Créer la grille'}</button>
        </div>
      </div>
    </div>
  );
}

// ═══ SÉCURITÉ · LIENS (SA-10.1, module épinglé de la maquette) — la durée de vie d'un lien EST une
// décision de sécurité : un jeton d'appairage qui traîne trois jours est une clé d'accès en circulation.
// ⚠️ SEULE la durée du jeton d'appairage est réglable ICI, parce que c'est la seule que la Console
// APPLIQUE elle-même (relue à chaque QR émis). L'invitation et la réinitialisation sont émises par l'IdP :
// on affiche la valeur EN VIGUEUR lue chez lui, sans prétendre pouvoir la changer d'ici (§23.0). ═══
const PAIRING_CHOICES = [4, 24, 72];
const PAIRING_WARN: Record<number, string> = {
  4: 'Court : la mise en service doit être faite dans la foulée — le QR devra souvent être régénéré.',
  24: 'Recommandé : le temps d’une intervention, pas plus.',
  72: 'Trois jours : un jeton qui traîne aussi longtemps est une clé d’accès en circulation. À réserver aux déploiements à distance.',
};
/** Rend une durée en millisecondes telle qu'un humain la dit (« 7 jours », « 30 min »). */
const humanMs = (ms: number): string => {
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.round(h / 24);
  return `${d} jour${d > 1 ? 's' : ''}`;
};

/**
 * Les durées de « Sécurité · liens », dans l'ordre du panneau. ⚠️ Le compteur du rail se DÉRIVE de cette liste : il
 * était écrit « 3 » à la main pour quatre durées affichées.
 */
const SECURITY_LINKS = [
  'Jeton de mise en service d’un appareil', 'Invitation à déposer un dossier', 'Invitation à créer un compte', 'Réinitialisation de mot de passe',
] as const;

function SecurityLinksPanel({ canWrite }: { canWrite: boolean }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.linkSettings>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.linkSettings().then((r) => { setData(r); setErr(null); }).catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);

  const setPairing = async (h: number) => {
    setBusy(true);
    try { await api.updateLinkSettings({ pairingTtlHours: h }); showToast(`Jeton de mise en service : ${h} h.`); await load(); }
    catch (e) { void alert({ title: 'Réglage refusé', message: (e as Error).message }); }
    finally { setBusy(false); }
  };
  const setDraftTtl = async (h: number) => {
    setBusy(true);
    try { await api.updateLinkSettings({ draftInviteTtlHours: h }); showToast(`Invitation de dépôt : ${h < 48 ? h + ' h' : Math.round(h / 24) + ' j'}.`); await load(); }
    catch (e) { void alert({ title: 'Réglage refusé', message: (e as Error).message }); }
    finally { setBusy(false); }
  };

  if (err) return <div className="dcard" style={{ padding: 18 }}><div className="err">Réglages indisponibles : {err}</div></div>;
  if (!data) return <div className="dcard" style={{ padding: 18 }}><span className="muted">Chargement des réglages…</span></div>;

  const card = { padding: '14px 16px', borderTop: '1px solid var(--ink200)' } as const;
  const title = { fontSize: 'var(--t-lead)', fontWeight: 700 } as const;
  const note = { fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.55 } as const;
  // Une durée qu'on ne peut pas régler d'ici se présente en LECTURE, avec le lieu où elle se règle.
  const readOnly = (label: string, value: string | null, why: string) => (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={title}>{label}</span>
        <span className="mono" style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: value ? 'var(--ink900)' : 'var(--wa-fg)' }}>{value ?? 'valeur inconnue'}</span>
        <span style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--r-pill)', background: 'var(--ne-bg)', color: 'var(--ne-fg)' }}>lecture seule</span>
      </div>
      <div style={note}>{why}</div>
    </div>
  );

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', background: 'var(--head)' }}>
        <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>Sécurité · liens</div>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, maxWidth: 560, lineHeight: 1.5 }}>
          Combien de temps un lien émis reste valable. Chacun ne sert qu'<b>une fois</b> : consommé, il ne rouvre rien.
        </div>
      </div>

      {/* Le SEUL réglage appliqué par la Console. */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={title}>{SECURITY_LINKS[0]}</span>
          <span className="mono" style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>{data.pairingTtlHours} h</span>
        </div>
        <div style={note}>Durée de validité du QR d'appairage. Relue à chaque émission — le changement vaut pour les jetons suivants, pas pour ceux déjà remis.</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {PAIRING_CHOICES.map((h) => {
            const on = data.pairingTtlHours === h;
            return (
              <button key={h} disabled={!canWrite || busy} onClick={() => void setPairing(h)}
                style={{ height: 30, padding: '0 13px', borderRadius: 'var(--r-xs)', cursor: !canWrite || busy ? 'default' : 'pointer', fontSize: 'var(--t-dense)', fontWeight: 700,
                  border: `1px solid ${on ? 'var(--teal-700)' : 'var(--ink200)'}`, background: on ? 'var(--teal-700)' : 'transparent', color: on ? '#fff' : 'var(--ink600)' }}>
                {h} h
              </button>
            );
          })}
        </div>
        <div style={{ ...note, color: data.pairingTtlHours >= 72 ? 'var(--wa-fg)' : 'var(--ink500)' }}>{PAIRING_WARN[data.pairingTtlHours] ?? `Valeur personnalisée (${data.pairingTtlHours} h).`}</div>
      </div>

      {/* Durée des INVITATIONS DE DÉPÔT — appliquée par la Console (relue à chaque émission). */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={title}>{SECURITY_LINKS[1]}</span>
          <span className="mono" style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>{data.draftInviteTtlHours} h</span>
        </div>
        <div style={note}>Lien du formulaire externe (nouveau client, nouveau magasin, complément). Remplir un dossier n'est pas scanner un QR : le destinataire peut s'y mettre le lendemain — d'où des durées en jours.</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {[24, 72, 168, 336].map((h) => {
            const on = data.draftInviteTtlHours === h;
            return (
              <button key={h} disabled={!canWrite || busy} onClick={() => void setDraftTtl(h)}
                style={{ height: 30, padding: '0 13px', borderRadius: 'var(--r-xs)', cursor: !canWrite || busy ? 'default' : 'pointer', fontSize: 'var(--t-dense)', fontWeight: 700,
                  border: `1px solid ${on ? 'var(--teal-700)' : 'var(--ink200)'}`, background: on ? 'var(--teal-700)' : 'transparent', color: on ? '#fff' : 'var(--ink600)' }}>
                {h < 48 ? `${h} h` : `${Math.round(h / 24)} j`}
              </button>
            );
          })}
        </div>
      </div>

      {readOnly(SECURITY_LINKS[2], data.idp ? humanMs(data.idp.inviteTtlMs) : null,
        data.idpError
          ? `Durée non lue : ${data.idpError}`
          : "Émise par l'IdP : c'est lui qui la fait respecter, elle se règle dans sa configuration — la Console l'affiche, elle ne la pilote pas.")}
      {readOnly(SECURITY_LINKS[3], data.idp ? humanMs(data.idp.resetTtlMs) : null,
        data.idpError
          ? `Durée non lue : ${data.idpError}`
          : "Volontairement courte : un lien de réinitialisation qui survit à la journée est une porte ouverte. Réglée dans le socle d'authentification.")}

      {!canWrite && <div style={{ padding: '10px 16px', borderTop: '1px solid var(--ink200)', fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Lecture seule — le réglage est réservé au super-admin.</div>}
    </div>
  );
}

// ═══ TYPES DE COMMERCE (SA-10.1/SA-2.3, maquette refTrades) — un métier = ASSEMBLAGE COCHABLE de
// listes de référence, injecté dans chaque NOUVELLE base magasin. Le SOCLE (civilités, moyens de
// paiement, motifs) est obligatoire : le serveur le ré-impose à chaque écriture, l'UI le montre
// coché-verrouillé. Le compteur « injecté par base » est CALCULÉ sur les lignes réelles des listes. ═══
//
// ⚠️ LES SECTIONS ÉTAIENT ÉCRITES ICI, EN DUR, ET C'ÉTAIT LE TROU LE MOINS VISIBLE DU CHANTIER.
//    Ce panneau ne rendait pas les listes présentes en base : il rendait trois catégories nommées à
//    la main et ne parcourait que LEURS clés. Une liste créée sans y figurer n'apparaissait dans
//    AUCUNE case à cocher — donc impossible à rattacher à un métier, donc jamais injectée nulle
//    part. Elle existait sans exister. Le classement est désormais un attribut de la LISTE.
// ⚠️ L'ORDRE DES SECTIONS SUIT LE RANG DE LEUR PREMIÈRE LISTE : il se règle donc là où l'on range
//    déjà les listes, sans second réglage à tenir. Le socle passe devant, quoi qu'il arrive.
const SOCLE_CAT = 'Socle obligatoire';
const tradeCats = (defs: RefListDef[] | null): { label: string; badge?: string; why?: string; keys: string[] }[] => {
  const par = new Map<string, { ord: number; keys: string[] }>();
  for (const d of [...(defs ?? [])].sort((a, b) => a.ord - b.ord)) {
    // Une liste sans classement ne se rattache à aucun métier — c'est le cas des codes NAF et des
    // modèles de consigne, qui ne quittent pas la Console.
    if (!d.category) continue;
    const e = par.get(d.category) ?? { ord: d.ord, keys: [] };
    e.keys.push(d.key);
    par.set(d.category, e);
  }
  return [...par.entries()]
    .sort((a, b) => (a[0] === SOCLE_CAT ? -1 : b[0] === SOCLE_CAT ? 1 : a[1].ord - b[1].ord))
    .map(([label, e]) => ({
      label,
      ...(label === SOCLE_CAT
        ? { badge: 'non décochable', why: 'Encaisser, tracer un motif, nommer un client : aucun métier ne vend sans (SPEC SA-10.1).' }
        : {}),
      keys: e.keys,
    }));
};

function TradesPanel({ trades, lists, defs, socle, canWrite, onChanged }: {
  trades: TradeTypeRow[] | null;
  lists: Map<string, ReferentialRow[]>;
  defs: RefListDef[] | null;
  /** Le socle, DIT PAR LE SERVEUR — c'est lui qui le ré-impose à l'écriture. En avoir une copie ici
   *  faisait deux vérités, dont une seule est appliquée. */
  socle: string[];
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [selId, setSelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addingTrade, setAddingTrade] = useState<{ code: string; label: string } | null>(null);
  const [editHead, setEditHead] = useState<{ label: string; description: string } | null>(null);

  // Les grilles sont cochables UNE PAR UNE ('size_grids:<code>') : une boutique chausseur prend
  // les pointures sans embarquer les tailles alpha.
  // Les sections, reconstruites des définitions — voir `tradeCats` pour l'ordre.
  const cats = useMemo(() => tradeCats(defs), [defs]);
  const grids = lists.get('size_grids') ?? [];
  const gridKeys = grids.map((g) => `size_grids:${g.code}`);
  const linesOf = (key: string): number => {
    if (key.startsWith('size_grids:')) {
      const v = grids.find((r) => r.code === key.split(':')[1])?.attrs?.values;
      return Array.isArray(v) ? v.length : 0;
    }
    return lists.get(key)?.length ?? 0;
  };
  const labelOf = (key: string): { label: string; note: string } => {
    if (key.startsWith('size_grids:')) {
      const g = grids.find((r) => r.code === key.split(':')[1]);
      const v = g?.attrs?.values;
      return { label: g?.label ?? key, note: Array.isArray(v) ? v.join(' · ') : '—' };
    }
    const d = defs?.find((x) => x.key === key);
    return { label: d?.label ?? key, note: `${lists.get(key)?.length ?? 0} valeur(s)` };
  };
  const sumOf = (tt: TradeTypeRow) => ({ n: tt.lists.length, lines: tt.lists.reduce((a, k) => a + linesOf(k), 0) });

  const t = trades?.find((x) => x.id === selId) ?? trades?.[0] ?? null;

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    setBusy(true);
    try { await fn(); showToast(okMsg); onChanged(); return true; }
    catch (e) { void alert({ title: 'Action impossible', message: (e as Error).message }); return false; }
    finally { setBusy(false); }
  };

  const toggle = (key: string) => {
    if (!t || socle.includes(key)) return;
    const next = t.lists.includes(key) ? t.lists.filter((k) => k !== key) : [...t.lists, key];
    void run(() => api.updateTrade(t.id, { lists: next }), 'Assemblage mis à jour.');
  };
  const removeTrade = async () => {
    if (!t) return;
    const ok = await confirm({ title: `Supprimer le type « ${t.label} » ?`, message: "Les magasins existants gardent leur métier — seul l'assemblage maître disparaît pour les futures mises en service.", confirmLabel: 'Supprimer' });
    if (ok) { const done = await run(() => api.deleteTrade(t.id), `« ${t.label} » supprimé.`); if (done) setSelId(null); }
  };
  const saveHead = async () => {
    if (!t || !editHead) return;
    const label = editHead.label.trim();
    if (!label) return;
    const done = await run(() => api.updateTrade(t.id, { label, description: editHead.description.trim() }), 'Type de commerce enregistré.');
    if (done) setEditHead(null);
  };
  const saveAddTrade = async () => {
    if (!addingTrade) return;
    const code = addingTrade.code.trim(); const label = addingTrade.label.trim();
    if (!code || !label) return;
    const done = await run(() => api.createTrade({ code, label, lists: [...socle] }), `Type « ${label} » créé (socle inclus).`);
    if (done) setAddingTrade(null);
  };

  // ⚠️ Honnêteté : source en échec ≠ liste vide — on le dit, on n'affiche pas un module vide rassurant.
  if (trades === null) return <div className="dcard" style={{ padding: 18 }}><span className="muted">Types de commerce indisponibles (chargement en échec) — rechargez la page.</span></div>;

  const inputSt = { height: 29, padding: '0 10px', borderRadius: 'var(--r-xs)', border: '1px solid var(--teal-500)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)' } as const;

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>Types de commerce</div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, maxWidth: 560, lineHeight: 1.5 }}>Un métier = l'assemblage des listes injectées dans chaque nouvelle base magasin. Le socle (paiements, motifs, civilités) est toujours inclus.</div>
        </div>
        <div style={{ flex: 1 }} />
        {canWrite && !addingTrade && (
          <button onClick={() => setAddingTrade({ code: '', label: '' })} className="btn-ghost" style={{ height: 32, padding: '0 13px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-dense)', fontWeight: 600 }}>Ajouter un type</button>
        )}
      </div>
      {canWrite && addingTrade && (
        <div style={{ display: 'flex', gap: 8, padding: '10px 18px', borderBottom: '1px solid var(--ink200)', background: 'color-mix(in srgb, var(--teal-500) 6%, transparent)', flexWrap: 'wrap' }}>
          <input autoFocus placeholder="code" className="mono" value={addingTrade.code} onChange={(e) => setAddingTrade({ ...addingTrade, code: e.target.value })} style={{ ...inputSt, width: 120 }} />
          <input placeholder="nom du métier" value={addingTrade.label} onChange={(e) => setAddingTrade({ ...addingTrade, label: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') void saveAddTrade(); }} style={{ ...inputSt, flex: 1, minWidth: 160 }} />
          <button className="btn btn-cta sm" disabled={busy || !addingTrade.code.trim() || !addingTrade.label.trim()} onClick={() => void saveAddTrade()} style={{ width: 'auto' }}>Créer</button>
          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '4px 10px', fontSize: 'var(--t-meta)' }} onClick={() => setAddingTrade(null)}>Annuler</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '236px minmax(0,1fr)' }}>
        {/* Nav des métiers (maquette : nom + méta « N listes · M lignes »). */}
        <div style={{ borderRight: '1px solid var(--ink200)', padding: 8 }}>
          {trades.map((tt) => {
            const sum = sumOf(tt);
            const on = t?.id === tt.id;
            return (
              <button key={tt.id} onClick={() => { setSelId(tt.id); setEditHead(null); }}
                style={{ width: '100%', display: 'block', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: 0, cursor: 'pointer', background: on ? 'var(--ink-100)' : 'transparent', color: 'inherit', textAlign: 'left', marginBottom: 2 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: on ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tt.label}</span>
                <span className="mono" style={{ display: 'block', fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 2 }}>{sum.n} listes · {sum.lines} lignes</span>
              </button>
            );
          })}
          {trades.length === 0 && <div style={{ padding: 10, fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>Aucun type — créez le premier.</div>}
        </div>
        {t ? (
          <div style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                {editHead ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input aria-label="Nom de la liste" value={editHead.label} onChange={(e) => setEditHead({ ...editHead, label: e.target.value })} style={{ ...inputSt, fontSize: 'var(--t-control)', fontWeight: 700 }} />
                    <input value={editHead.description} placeholder="description" onChange={(e) => setEditHead({ ...editHead, description: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') void saveHead(); if (e.key === 'Escape') setEditHead(null); }} style={inputSt} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-cta sm" disabled={busy || !editHead.label.trim()} onClick={() => void saveHead()} style={{ width: 'auto' }}>Enregistrer</button>
                      <button className="ghost btn-ghost" style={{ width: 'auto', padding: '4px 10px', fontSize: 'var(--t-meta)' }} onClick={() => setEditHead(null)}>Annuler</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, letterSpacing: '-.01em' }}>{t.label} <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', fontWeight: 500 }}>· {t.code}</span></div>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5, maxWidth: 440 }}>{t.description || '— sans description'}</div>
                  </>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>injecté par base</div>
                <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.02em', marginTop: 3 }}>{sumOf(t).n}<span style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink500)' }}> listes · {sumOf(t).lines} lignes</span></div>
                {canWrite && !editHead && (
                  <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button className="ghost btn-ghost" style={{ width: 'auto', padding: '3px 9px', fontSize: 'var(--t-meta)' }} onClick={() => setEditHead({ label: t.label, description: t.description })}>Modifier</button>
                    <button disabled={busy} onClick={() => void removeTrade()} className="btn-ghost danger" style={{ height: 24, padding: '0 9px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontWeight: 600 }}>Supprimer</button>
                  </div>
                )}
              </div>
            </div>

            {[...cats, { label: 'Grilles de taille', keys: gridKeys } as (typeof cats)[number]].map((cat) => (
              <div key={cat.label} style={{ marginTop: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, flexWrap: 'wrap' }}>
                  <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{cat.label}</span>
                  {cat.badge && <span title={cat.why} style={{ fontSize: 'var(--t-eyebrow)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--r-pill)', background: 'var(--wa-bg)', color: 'var(--wa-fg)', cursor: 'help' }}>{cat.badge}</span>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8 }}>
                  {cat.keys.map((key) => {
                    const locked = socle.includes(key);
                    const on = locked || t.lists.includes(key);
                    const info = labelOf(key);
                    return (
                      <button key={key} disabled={!canWrite || locked || busy} onClick={() => toggle(key)}
                        title={locked ? 'Socle obligatoire — non décochable (SPEC SA-10.1).' : undefined}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 'var(--r-sm)', textAlign: 'left', cursor: !canWrite || locked ? 'default' : 'pointer', border: `1px solid ${on ? 'color-mix(in srgb, var(--teal-500) 40%, transparent)' : 'var(--ink200)'}`, background: on ? 'color-mix(in srgb, var(--teal-500) 7%, transparent)' : 'transparent', color: 'inherit' }}>
                        <span style={{ width: 18, height: 18, borderRadius: 'var(--r-r5)', flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 800, background: on ? 'var(--teal-700)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink400)' }}>{on ? '✓' : ''}</span>
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-dense)', fontWeight: 600 }}>{info.label}{locked && <Icon name="lock" size={11} />}</span>
                          <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.note}</span>
                        </span>
                        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)', flexShrink: 0 }}>{linesOf(key)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Bandeau maquette : la portée d'un décochage — nouveaux magasins seulement ; la propagation ne retire rien. */}
            <div style={{ display: 'flex', gap: 9, marginTop: 18, padding: '11px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--in-bg)' }}>
              <span style={{ color: 'var(--in-fg)', display: 'grid', marginTop: 1 }}><Icon name="info" size={14} /></span>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--in-fg)', lineHeight: 1.55 }}>Un décochage ne retire rien aux magasins déjà en service : l'assemblage ne s'applique qu'aux bases créées ensuite. La propagation (écart prévisualisé, magasin par magasin) ajoute aux magasins en service ce qui leur manque ; elle ne leur retire jamais une liste.</div>
            </div>
            {!canWrite && <div style={{ marginTop: 12, fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>Lecture seule — l'édition des types de commerce est réservée au super-admin.</div>}
          </div>
        ) : (
          <div style={{ padding: 18 }}><EmptyState icon="store" title="Aucun type de commerce" note="Créez le premier métier — il naîtra avec le socle obligatoire, puis cochez ses listes." /></div>
        )}
      </div>
    </div>
  );
}

// ── PROPAGATION (SA-10.2, seconde moitié) : analyse par magasin → arbitrage des conflits → application.
// ⚠️ Les trois règles de sûreté sont tenues au SERVEUR (jamais d'écrasement sans imposition, jamais
// l'ordre, jamais de suppression) — l'écran les MONTRE : chaque conflit affiche les deux valeurs, et
// la valeur du magasin est le choix par défaut.
/**
 * LE RÉFÉRENTIEL DES COMMUNES — code postal → ville, dans toutes les bases magasin.
 *
 * POURQUOI IL EST ICI. C'est un référentiel comme les autres du point de vue de l'exploitant, à une
 * différence près : **il ne s'édite pas**. Il vient de la source officielle (API du découpage
 * administratif) et il est le même pour tout le monde — il n'y a donc ni liste à tenir ni conflit à
 * arbitrer. Ce qu'un humain décide ici, c'est QUAND il descend et VERS QUI.
 *
 * ⚠️ POURQUOI PAS AU BACK-OFFICE : sa porte d'écriture humaine n'écrit que dans le magasin de la
 *    SESSION. Un écran là-bas ne pourrait jamais servir le parc.
 *
 * ⚠️ ANALYSER N'ÉCRIT RIEN, et c'est le point : l'écart est MESURÉ dans chaque base avant qu'on
 *    propose quoi que ce soit. Un outil qui agit au premier clic est un outil qu'on hésite à lancer.
 *
 * ⚠️ TROIS ÉTATS, JAMAIS DEUX : à jour · un écart à combler · **une base qu'on n'a pas pu lire**.
 *    Confondre le troisième avec le premier ferait passer un magasin injoignable pour un magasin
 *    servi, et personne ne le saurait avant le comptoir (§23.0).
 */
function CommunesPanel({ canWrite }: { canWrite: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [source, setSource] = useState<{ communes: number; pairs: number } | null>(null);
  const [stores, setStores] = useState<CommunesStore[] | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<CommunesApplyResult[] | null>(null);

  /** Un magasin qu'on peut cocher : lu sans erreur, et avec quelque chose à recevoir. */
  const actionable = (s: CommunesStore) => !s.skipped && !s.error && s.missing > 0;

  const analyze = async () => {
    setBusy(true); setErr(null); setResults(null); setStores(null); setSource(null);
    try {
      const r = await api.analyzeCommunes();
      setSource(r.source); setStores(r.stores);
      // Pré-cochés = ceux qui ont un écart. Cocher un magasin à jour ferait croire qu'une écriture l'attend.
      setSel(new Set(r.stores.filter(actionable).map((x) => x.id)));
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setErr(null);
    try { const r = await api.applyCommunes([...sel]); setResults(r.results); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };
  const toggle = (id: string) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>Communes (codes postaux)</div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5 }}>
            Le référentiel officiel des communes, descendu dans les bases magasin puis dans les caisses —
            c'est lui qui complète la ville d'une adresse client. Il ne s'édite pas : il vient de la
            source publique. L'analyse <b style={{ color: 'var(--ink700)' }}>n'écrit rien</b>.
          </div>
        </div>
        <button onClick={() => void analyze()} disabled={!canWrite || busy}
          title={canWrite ? undefined : "Réservée au super-admin — l'analyse ouvre la base de chaque magasin."}
          className="btn-ghost" style={{ height: 34, padding: '0 14px', borderRadius: 'var(--r-sm)', fontSize: 'var(--t-body)', fontWeight: 600, whiteSpace: 'nowrap', ...(canWrite ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}>
          {busy && !stores ? 'Analyse…' : stores ? 'Analyser à nouveau' : 'Analyser'}
        </button>
      </div>

      {err && <div className="err" role="alert" style={{ padding: '10px 16px', marginTop: 0 }}>{err}</div>}
      {busy && !stores && !err && <div className="muted" style={{ padding: '12px 16px' }}>Lecture de la source, puis de chaque base magasin… (une connexion à la fois)</div>}

      {/* Ce que la source a rendu : le chiffre qui dit si elle a répondu ENTIÈREMENT. */}
      {source && (
        <div style={{ padding: '0 16px 10px', fontSize: 'var(--t-note)', color: 'var(--ink600)' }}>
          Source : <b>{source.communes.toLocaleString()}</b> communes → <b>{source.pairs.toLocaleString()}</b> couples (code postal, ville).
        </div>
      )}

      {stores && !results && (
        <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {stores.length === 0 && <EmptyState icon="store" title="Aucun magasin" note="Rien à propager : aucun magasin actif avec une base." />}
          {stores.map((s) => (
            <div key={s.id} style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', padding: '9px 12px', opacity: s.skipped ? 0.6 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <input type="checkbox" aria-label={`Propager vers ${s.name}`} checked={sel.has(s.id)} disabled={!actionable(s) || busy} onChange={() => toggle(s.id)} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700 }}>{s.name}</span>
                  <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 8 }}>{s.gln}</span>
                </div>
                {s.skipped && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{s.skipped}</span>}
                {/* ⚠️ UNE BASE ILLISIBLE N'EST PAS UNE BASE À JOUR : son message RÉEL, en rouge. */}
                {s.error && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)' }}>base illisible — {s.error}</span>}
                {!s.skipped && !s.error && (
                  <span className="mono" style={{ fontSize: 'var(--t-sub)', color: s.missing ? 'var(--ink700)' : 'var(--ink500)' }}>
                    {s.missing === 0
                      ? `à jour — ${s.inStore.toLocaleString()} commune${s.inStore > 1 ? 's' : ''}`
                      : `${s.missing.toLocaleString()} à écrire — ${s.inStore.toLocaleString()} déjà en base`}
                  </span>
                )}
              </div>
            </div>
          ))}
          {stores.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
              <button className="btn btn-cta sm" disabled={busy || sel.size === 0} onClick={() => void apply()} style={{ width: 'auto' }}>
                {busy ? 'Propagation…' : `Propager vers ${sel.size} magasin${sel.size > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Résultats RÉELS : ce que le producteur a écrit, ou le motif nommé — rien de présumé. */}
      {results && (
        <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {results.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)' }}>
              <Icon name={r.error ? 'alert' : 'check'} size={15} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700 }}>{r.name}</span>
                {r.gln && <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 8 }}>{r.gln}</span>}
              </div>
              {r.error
                ? <span style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)' }}>{r.error}</span>
                : <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink700)' }}>{r.note ?? `${(r.written ?? 0).toLocaleString()} ligne(s) écrite(s)`}</span>}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-ghost sm" onClick={() => void analyze()} style={{ width: 'auto', height: 30, padding: '0 12px', borderRadius: 'var(--r-xs)' }}>Réanalyser</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PropagationModal({ onClose }: { onClose: () => void }) {
  const [stores, setStores] = useState<PropagationStoreDiff[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  // Impositions par clé composée magasin|conflit — le défaut est TOUJOURS « garder le magasin ».
  const [impose, setImpose] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<PropagationApplyResult[] | null>(null);

  useEffect(() => {
    let alive = true;
    api.analyzePropagation()
      .then((r) => {
        if (!alive) return;
        setStores(r.stores);
        // Pré-sélection = les magasins qui ont quelque chose à recevoir. Un magasin à jour n'a rien
        // à cocher — le pré-cocher ferait croire qu'une écriture l'attend.
        setSel(new Set(r.stores.filter((s) => (s.adds?.length ?? 0) + (s.conflicts?.length ?? 0) > 0).map((s) => s.id)));
      })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  const toggleStore = (id: string) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleImpose = (storeId: string, key: string) => setImpose((p) => {
    const n = new Set(p); const k = `${storeId}|${key}`;
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const apply = async () => {
    if (!stores) return;
    setBusy(true); setErr(null);
    try {
      const asks = [...sel].map((id) => ({
        id,
        impose: [...impose].filter((k) => k.startsWith(`${id}|`)).map((k) => k.slice(id.length + 1)),
      }));
      const r = await api.applyPropagation(asks);
      setResults(r.results);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const masterDetailOf = (it: RefDiffItem): string | null =>
    (it.masterDetail && it.masterDetail !== it.storeDetail ? it.masterDetail : null);

  return (
    <div className="modalwrap" onClick={busy ? undefined : onClose}>
      <div className="modal" style={{ width: 'min(94vw, 640px)', maxHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <h3>Propagation aux magasins existants</h3>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: -4, lineHeight: 1.5 }}>
          Les <b>ajouts</b> s’appliquent tels quels. Un <b>conflit</b> (valeur personnalisée par le magasin)
          garde la version du magasin, sauf imposition explicite. L’ordre et les valeurs hors maître ne sont jamais touchés.
        </div>

        {err && <div className="err" style={{ marginTop: 12 }}>{err}</div>}
        {!stores && !err && <div className="muted" style={{ marginTop: 16 }}>Analyse des bases magasin… (une connexion par magasin)</div>}

        {stores && !results && (
          <div style={{ marginTop: 12, overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stores.length === 0 && <EmptyState icon="store" title="Aucun magasin" note="Rien à propager : aucun magasin déclaré." />}
            {stores.map((s) => {
              const nAdds = s.adds?.length ?? 0;
              const nConf = s.conflicts?.length ?? 0;
              const upToDate = !s.skipped && !s.error && nAdds + nConf === 0;
              return (
                <div key={s.id} style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', opacity: s.skipped ? 0.6 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', flexWrap: 'wrap' }}>
                    {!s.skipped && !s.error && (
                      <button onClick={() => !upToDate && toggleStore(s.id)} disabled={upToDate} className="btn-icon sm"
                        title={upToDate ? 'Magasin à jour — rien à appliquer' : undefined}
                        style={{ color: sel.has(s.id) ? 'var(--ok-fg)' : 'var(--ink400)' }}>{sel.has(s.id) ? '✓' : '○'}</button>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700 }}>{s.name}</span>
                      <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 8 }}>{s.gln}</span>
                    </div>
                    {s.skipped && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{s.skipped}</span>}
                    {s.error && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)' }}>{s.error}</span>}
                    {!s.skipped && !s.error && (
                      <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
                        {nAdds} ajout{nAdds > 1 ? 's' : ''} · {nConf} conflit{nConf > 1 ? 's' : ''} · {s.unchanged ?? 0} à jour{(s.extras?.length ?? 0) > 0 ? ` · ${s.extras!.length} hors maître` : ''}
                      </span>
                    )}
                    {nConf > 0 && (
                      <button className="btn-link marque" style={{ fontSize: 'var(--t-meta)' }} onClick={() => setOpen(open === s.id ? null : s.id)}>
                        {open === s.id ? 'replier' : 'arbitrer'}
                      </button>
                    )}
                  </div>
                  {/* Le magasin n'a pas résolu son métier : le socle seul est comparé — dit tel quel. */}
                  {s.note && !s.skipped && !s.error && <div style={{ padding: '0 12px 9px', fontSize: 'var(--t-meta)', color: 'var(--wa-fg)' }}>{s.note}</div>}
                  {open === s.id && (s.conflicts ?? []).map((c) => {
                    const on = impose.has(`${s.id}|${c.key}`);
                    return (
                      <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderTop: '1px solid var(--ink100)', flexWrap: 'wrap' }}>
                        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', width: 110, flexShrink: 0 }}>{c.code}</span>
                        <span style={{ fontSize: 'var(--t-dense)', minWidth: 0, flex: 1 }}>
                          <b>{c.storeLabel}</b>{c.storeDetail && c.storeDetail !== c.masterDetail ? <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}> {c.storeDetail}</span> : null}
                          <span style={{ color: 'var(--ink400)' }}> → </span>{c.masterLabel}{masterDetailOf(c) ? <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}> {masterDetailOf(c)}</span> : null}
                        </span>
                        <div className="seg-track">
                          <button type="button" className={on ? 'seg' : 'seg active'} onClick={() => on && toggleImpose(s.id, c.key)}>Garder le magasin</button>
                          <button type="button" className={on ? 'seg active' : 'seg'} onClick={() => !on && toggleImpose(s.id, c.key)}>Imposer le maître</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Résultats RÉELS par magasin — la journalisation BO dit son état, jamais présumée. */}
        {results && (
          <div style={{ marginTop: 12, overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {results.map((r) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)' }}>
                <span style={{ color: r.error ? 'var(--da-fg)' : 'var(--ok-fg)', flexShrink: 0 }}>{r.error ? '✕' : '✓'}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 'var(--t-dense)', fontWeight: 700 }}>{r.name ?? r.id}</div>
                  {r.error
                    ? <div style={{ fontSize: 'var(--t-note)', color: 'var(--da-fg)' }}>{r.error}</div>
                    : <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
                        {r.added ?? 0} ajouté{(r.added ?? 0) > 1 ? 's' : ''} · {r.imposed ?? 0} imposé{(r.imposed ?? 0) > 1 ? 's' : ''}
                        {r.journal && <span style={{ color: r.journal.ok ? undefined : 'var(--wa-fg)' }}> · {r.journal.note}</span>}
                      </div>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="modalfoot">
          <button className="ghost btn-ghost" onClick={onClose} disabled={busy}>{results ? 'Fermer' : 'Annuler'}</button>
          {!results && stores && (
            <button className="btn btn-cta sm" disabled={busy || sel.size === 0} onClick={() => void apply()} style={{ width: 'auto' }}>
              {busy ? 'Application…' : `Appliquer à ${sel.size} magasin${sel.size > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
